import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, Pencil, User, Clock, Lock, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { NotesPanel } from '../NotesPanel';
import { CandidatEditDialog } from '../CandidatEditDialog';
import { AbandonPanel, AbandonBadge } from './AbandonPanel';
import { DifficultesHistorique } from './DifficultesHistorique';
import { AppreciationPanel } from './AppreciationPanel';
import { PositionnementPanel } from './PositionnementPanel';
import { SynthesesBilansPanel } from './SynthesesBilansPanel';
import { ParcoursTimeline } from './ParcoursTimeline';
import { ProgressionTests } from '../suivi/ProgressionTests';
import { TrendingUp } from 'lucide-react';

const Field = ({ label, value }) => <div className="min-w-0"><span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span><p className="text-sm font-medium text-slate-800 truncate">{value || '—'}</p></div>;

// Assiduité : uniquement le taux de présence sur les journées de l'AF déjà effectuées
const fmtD = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');
const Kpi = ({ label, value, sub, warn, testId }) => (
  <div className={`rounded-lg px-3 py-2 ${warn ? 'bg-red-50' : 'bg-slate-50'}`} data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-base font-bold ${warn ? 'text-red-700' : 'text-slate-800'}`}>{value}</p>
    {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
  </div>
);

const AssiduiteTaux = ({ stats }) => {
  const s = stats || {};
  const [detail, setDetail] = useState(false);
  const taux = s.taux_presence;
  const n = s.nb_journees_effectuees || 0;
  const color = taux == null ? 'text-slate-400' : taux >= 90 ? 'text-emerald-600' : taux >= 75 ? 'text-amber-600' : 'text-red-600';
  const absences = s.absences || [];
  const retards = s.retards || [];
  return (
    <Card className="border-0 shadow-sm" data-testid="assiduite-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> Assiduité sur l'action</h2>
            <p className="text-xs text-slate-400 mt-0.5">{n > 0 ? `${s.nb_presences || 0} présence(s) sur ${n} journée(s) effectuée(s)${s.nb_retards ? ` · ${s.nb_retards} retard(s)` : ''}` : 'Aucune journée effectuée pour le moment'}</p>
          </div>
          <div className="flex items-center gap-3">
            <p className={`text-3xl font-bold font-['Plus_Jakarta_Sans'] ${color}`} data-testid="assiduite-taux">{taux == null ? '—' : `${taux} %`}</p>
            <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => setDetail(d => !d)} data-testid="assiduite-detail-btn">{detail ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} {detail ? 'Masquer' : 'Voir le détail'}</Button>
          </div>
        </div>
        {detail && (
          <div className="mt-4 space-y-3" data-testid="assiduite-detail">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="Présences" value={`${s.nb_presences || 0}/${n}`} sub="journées effectuées" testId="assiduite-kpi-presences" />
              <Kpi label="Absences" value={s.nb_absences || 0} sub={s.heures_absence ? `${s.heures_absence} h d'absence` : null} warn={(s.nb_absences || 0) > 0} testId="assiduite-kpi-absences" />
              <Kpi label="Retards" value={s.nb_retards || 0} sub={s.minutes_retard ? `${s.minutes_retard} min cumulées` : null} warn={(s.nb_retards || 0) > 0} testId="assiduite-kpi-retards" />
              <Kpi label="Taux de présence" value={taux == null ? '—' : `${taux} %`} testId="assiduite-kpi-taux" />
            </div>
            {absences.length === 0 && retards.length === 0 ? <p className="text-xs text-slate-400 italic">Aucune absence ni retard enregistré.</p> : (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-red-600 font-semibold border-b bg-red-50/50">Absences ({absences.length})</p>
                  <ul className="divide-y text-xs">
                    {absences.length === 0 && <li className="px-3 py-2 text-slate-400 italic">Aucune</li>}
                    {absences.map((x, i) => (
                      <li key={i} className="px-3 py-1.5 flex items-start gap-2" data-testid={`assiduite-absence-${x.date}`}>
                        <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                        <span><span className="font-medium text-slate-800">{fmtD(x.date)}</span> · {x.heures} h{x.via_aft ? <span className="ml-1 text-cyan-700">(via {x.via_aft})</span> : ''}{x.justifie ? <span className="ml-1 text-emerald-700">· justifiée</span> : <span className="ml-1 text-slate-400">· non justifiée</span>}{x.motif && <span className="block text-slate-500 italic">{x.motif}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-slate-200">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-amber-600 font-semibold border-b bg-amber-50/50">Retards ({retards.length})</p>
                  <ul className="divide-y text-xs">
                    {retards.length === 0 && <li className="px-3 py-2 text-slate-400 italic">Aucun</li>}
                    {retards.map((x, i) => (
                      <li key={i} className="px-3 py-1.5 flex items-start gap-2" data-testid={`assiduite-retard-${x.date}`}>
                        <Clock className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                        <span><span className="font-medium text-slate-800">{fmtD(x.date)}</span> · arrivée {x.heure_arrivee || '—'} ({x.minutes} min){x.via_aft ? <span className="ml-1 text-cyan-700">(via {x.via_aft})</span> : ''}{x.motif && <span className="block text-slate-500 italic">{x.motif}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const FicheCandidatAction = ({ dossier, candidat, assiduite, onBack, onCandidatUpdated, onAbandonChanged }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(searchParams.get('edit') === '1');
  const [abandon, setAbandon] = useState(candidat.abandon || null);
  const [refreshKey, setRefreshKey] = useState(0);
  const canManage = ['admin', 'super_admin', 'responsable_pedagogique', 'administratif'].includes(user?.role);
  const canEdit = !['candidat', 'client', 'financeur'].includes(user?.role);
  const c = candidat;
  const closeEdit = (o) => { setEditOpen(o); if (!o && searchParams.get('edit')) { searchParams.delete('edit'); setSearchParams(searchParams, { replace: true }); } };

  return (
    <div className="max-w-7xl mx-auto space-y-5 p-6" data-testid="fiche-candidat-action">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="fiche-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <h1 className="text-xl font-bold text-slate-900">{c.nom} {c.prenom}</h1>
        <AbandonBadge abandon={abandon} />
        {canEdit && <Button type="button" variant="outline" size="sm" className="text-xs ml-auto" onClick={() => setEditOpen(true)} data-testid="edit-candidat-fiche-btn"><Pencil className="w-3 h-3 mr-1" /> Modifier la fiche</Button>}
      </div>
      <CandidatEditDialog candidat={c} open={editOpen} onOpenChange={closeEdit} onSaved={onCandidatUpdated} />

      {/* Notes libres : internes Agrope, jamais diffusées (rapports, PDF, portails) */}
      {canEdit && (
        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-1" data-testid="notes-internes-bloc">
          <p className="px-4 pt-2 text-[11px] text-amber-800 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Note interne Agrope — non diffusée dans les rapports, bilans, PDF ni sur les portails candidat / entreprise / financeur.</p>
          <NotesPanel dossierId={dossier.id} candidatId={c.id} title="Notes libres sur le candidat (interne)" testId="notes-candidat" />
        </div>
      )}

      <AbandonPanel dossierId={dossier.id} candidat={c} abandon={abandon} canManage={canManage} onChanged={(a) => { setAbandon(a); setRefreshKey(k => k + 1); onAbandonChanged?.(c.id, a); }} />
      {canManage && <DifficultesHistorique dossierId={dossier.id} candidatId={c.id} />}

      <Card className="border-0 shadow-sm" data-testid="fiche-renseignement">
        <CardContent className="p-4">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2 mb-3"><User className="w-3.5 h-3.5 text-indigo-500" /> Fiche de renseignement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2">
            <Field label="Civilité" value={c.civilite} /><Field label="Date de naissance" value={c.date_naissance} />
            <Field label="Email" value={c.email} /><Field label="Téléphone" value={c.telephone} />
            <Field label="Entreprise utilisatrice" value={c.entreprise} /><Field label="Poste" value={c.poste} />
            <Field label="Adresse" value={[c.adresse, c.code_postal, c.ville].filter(Boolean).join(', ')} />
          </div>
        </CardContent>
      </Card>

      <AssiduiteTaux stats={assiduite?.candidats?.[c.id]} />
      <Card className="border-0 shadow-sm" data-testid="progression-panel">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" /> Progression aux tests (tentatives)</h2>
          <ProgressionTests dossierId={dossier.id} candidatId={c.id} />
        </CardContent>
      </Card>

      <PositionnementPanel dossierId={dossier.id} candidatId={c.id} canEdit={canEdit} />

      <AppreciationPanel dossierId={dossier.id} candidatId={c.id} canEdit={canEdit} />

      <SynthesesBilansPanel dossierId={dossier.id} candidatId={c.id} candidatNom={`${c.nom} ${c.prenom}`} canEdit={canEdit} />

      <ParcoursTimeline dossierId={dossier.id} candidatId={c.id} refreshKey={refreshKey} />
    </div>
  );
};
